import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiAward, FiClock, FiUsers, FiDollarSign, FiStar, FiArrowRight, FiCalendar } from 'react-icons/fi';
import { getApiUrl } from '../utils/api';
import StatusIndicator from '../components/common/StatusIndicator';

interface Competition {
  id: number;
  name: string;
  description: string | null;
  start_date: string;
  end_date: string;
  submission_deadline: string | null;
  evaluation_type: string;
  prize_pool: number | null;
  entry_cost_credits: number;
  min_reputation_tier: string | null;
  judging_weights: Record<string, number>;
  is_active: boolean;
  is_featured: boolean;
  entry_count: number;
  status: 'upcoming' | 'active' | 'closed';
}

export default function CompetitionsPage() {
  const [competitions, setCompetitions] = useState<Competition[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'active' | 'upcoming' | 'closed'>('active');
  const navigate = useNavigate();

  useEffect(() => {
    fetchCompetitions();
  }, []);

  const fetchCompetitions = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${getApiUrl()}/api/v1/competitions?active_only=false`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCompetitions(data);
      }
    } catch (err) {
      console.error('Error fetching competitions:', err);
    } finally {
      setLoading(false);
    }
  };

  const getTimeRemaining = (endDate: string): string => {
    const end = new Date(endDate);
    const now = new Date();
    const diff = end.getTime() - now.getTime();

    if (diff <= 0) return 'Ended';

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

    if (days > 0) return `${days}d ${hours}h left`;
    return `${hours}h left`;
  };

  const getStatusColor = (status: string): 'ready' | 'busy' | 'idle' => {
    switch (status) {
      case 'active': return 'ready';
      case 'upcoming': return 'busy';
      default: return 'idle';
    }
  };

  const filteredCompetitions = competitions.filter(c => {
    if (filter === 'all') return true;
    return c.status === filter;
  });

  const featuredCompetition = competitions.find(c => c.is_featured && c.status === 'active');

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <FiAward className="w-8 h-8 text-yellow-500" />
            Competitions
          </h1>
          <p className="mt-2 text-gray-400">
            Compete against other quants and prove your strategy's edge
          </p>
        </div>

        {/* Featured Competition */}
        {featuredCompetition && (
          <div
            onClick={() => navigate(`/dashboard/competitions/${featuredCompetition.id}`)}
            className="mb-8 p-6 bg-gradient-to-r from-yellow-900/20 to-orange-900/20 border border-yellow-800/50 rounded-lg cursor-pointer hover:border-yellow-700 transition-colors"
          >
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <FiStar className="w-5 h-5 text-yellow-500" />
                  <span className="text-xs text-yellow-500 font-medium uppercase tracking-wider">Featured Competition</span>
                </div>
                <h2 className="text-2xl font-semibold text-white mb-2">{featuredCompetition.name}</h2>
                <p className="text-gray-400 mb-4">{featuredCompetition.description}</p>
                <div className="flex items-center gap-6 text-sm">
                  {featuredCompetition.prize_pool && (
                    <div className="flex items-center gap-2 text-yellow-400">
                      <FiDollarSign className="w-4 h-4" />
                      <span>${featuredCompetition.prize_pool.toLocaleString()} Prize Pool</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-gray-400">
                    <FiUsers className="w-4 h-4" />
                    <span>{featuredCompetition.entry_count} entries</span>
                  </div>
                  <div className="flex items-center gap-2 text-loss">
                    <FiClock className="w-4 h-4" />
                    <span>{getTimeRemaining(featuredCompetition.end_date)}</span>
                  </div>
                </div>
              </div>
              <button className="flex items-center gap-2 px-4 py-2 bg-yellow-600 text-black font-medium rounded-md hover:bg-yellow-500 transition-colors">
                View Details
                <FiArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Filter Tabs */}
        <div className="flex items-center gap-2 mb-6">
          {(['all', 'active', 'upcoming', 'closed'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 text-sm rounded-md transition-colors ${
                filter === f
                  ? 'bg-white text-black'
                  : 'bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700'
              }`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>

        {/* Competition List */}
        {filteredCompetitions.length === 0 ? (
          <div className="text-center py-12 bg-gray-900 border border-gray-800 rounded-lg">
            <FiAward className="w-12 h-12 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">No {filter === 'all' ? '' : filter} competitions found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredCompetitions.map((comp) => (
              <div
                key={comp.id}
                onClick={() => navigate(`/dashboard/competitions/${comp.id}`)}
                className="bg-gray-900 border border-gray-800 rounded-lg p-5 hover:border-gray-700 transition-colors cursor-pointer"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-medium text-white text-lg">{comp.name}</h3>
                    {comp.description && (
                      <p className="text-sm text-gray-400 mt-1 line-clamp-2">{comp.description}</p>
                    )}
                  </div>
                  <StatusIndicator
                    status={getStatusColor(comp.status)}
                    label={comp.status.charAt(0).toUpperCase() + comp.status.slice(1)}
                    showLabel
                    size="sm"
                  />
                </div>

                <div className="grid grid-cols-3 gap-4 text-sm mb-4">
                  {comp.prize_pool && (
                    <div>
                      <p className="text-gray-500">Prize Pool</p>
                      <p className="text-white font-medium">${comp.prize_pool.toLocaleString()}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-gray-500">Entries</p>
                    <p className="text-white font-medium">{comp.entry_count}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Entry Cost</p>
                    <p className="text-white font-medium">
                      {comp.entry_cost_credits > 0 ? `${comp.entry_cost_credits} credits` : 'Free'}
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-3 border-t border-gray-800">
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <FiCalendar className="w-3 h-3" />
                    <span>
                      {new Date(comp.start_date).toLocaleDateString()} - {new Date(comp.end_date).toLocaleDateString()}
                    </span>
                  </div>
                  {comp.status === 'active' && (
                    <span className="text-xs text-loss font-medium">
                      {getTimeRemaining(comp.end_date)}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Call to action for users with no entries */}
        <div className="mt-8 p-6 bg-gray-900 border border-gray-800 rounded-lg text-center">
          <h3 className="text-lg font-medium text-white mb-2">Ready to compete?</h3>
          <p className="text-gray-400 mb-4">
            Create a strategy, backtest it, and submit to any active competition
          </p>
          <button
            onClick={() => navigate('/dashboard/strategies')}
            className="px-6 py-2 bg-white text-black font-medium rounded-md hover:bg-gray-100 transition-colors"
          >
            View My Strategies
          </button>
        </div>
      </div>
    </div>
  );
}
